import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { gettext } from '@eventbrite/i18n';

import * as analytics from '@eventbrite/site-analytics';
import { deepKeysToCamel } from '@eventbrite/transformation-utils';
import { EmptyState } from '@eventbrite/eds-empty-state';
import { DiamondGraphic } from '@eventbrite/eds-iconography';
import { GhostGraphic } from '@eventbrite/eds-iconography';

import { fetchEventStructuredContentApi } from '../api/structured-content';
import {
    GA_DEFAULT_CATEGORY,
    GA_ACTION_PAGE_LOAD,
    INTERVAL_TO_REFRESH_MODULES,
    MODULE_TYPE_FILE,
} from '../constants';

import { hasModuleOfType, getExtraData } from '../utils';

import {
    FileModule,
    ImageModule,
    InplayerModule,
    LivestreamModule,
    TextModule,
    VideoModule,
    WebinarModule,
    ZoomModule,
} from './modules';

const moduleTypeMap = {
    file: FileModule,
    image: ImageModule,
    inplayer_livestream: InplayerModule,
    livestream: LivestreamModule,
    text: TextModule,
    video: VideoModule,
    webinar: WebinarModule,
    zoom_mapping: ZoomModule,
};

const DownloadDisclaimer = () => (
    <div className="eds-align--center eds-l-pad-top-8 eds-l-mar-bot-14 eds-text-bs">
        <p>
            {gettext(
                'Use of downloaded materials may be subject to restrictions. Please contact the organizer for details.',
            )}
        </p>
    </div>
);

export const DigitalContent404 = () => (
    <div className="dc-empty-state eds-fx--fade-in eds-align--center eds-l-pad-vert-10">
        <EmptyState
            graphicType={<GhostGraphic />}
            title={gettext('Online Event Page not found')}
            description={gettext(
                'Links for this event are currently not available.',
            )}
        />
    </div>
);

export const DigitalContentEmptyState = ({ eventUrl, organizerUrl }) => (
    <div className="dc-empty-state eds-fx--fade-in eds-align--center eds-l-pad-vert-10">
        <EmptyState
            graphicType={<DiamondGraphic />}
            title={gettext('We’re sorry, this page isn’t available.')}
            description={
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: gettext(
                            'Check the <a href="%(eventUrl)s">event listing</a> or <a href="%(organizerUrl)s">contact the organizer</a> for more details.',
                            {
                                eventUrl,
                                organizerUrl,
                            },
                        ).toString(),
                    }}
                />
            }
        />
    </div>
);

export default class DigitalContent extends PureComponent {
    static props = {
        event: PropTypes.shape({
            end: PropTypes.string,
            id: PropTypes.string.isRequired,
            isSeries: PropTypes.bool,
            start: PropTypes.string,
        }).isRequired,
        hideVirtualVenueContent: PropTypes.bool.isRequired,
        modules: PropTypes.arrayOf(PropTypes.object),
        pagination: PropTypes.object,

        // ticket classes may actually come from the series parent, so we can't rely on the event's ticket classes
        ticketClasses: PropTypes.arrayOf(PropTypes.number),
        userFullName: PropTypes.string,
        userEmail: PropTypes.string,
        zoomSdkChromeOriginTrialsToken: PropTypes.string,
    };

    static defaultProps = {
        ticketClasses: [],
        hideVirtualVenueContent: false,
        modules: [],
        pagination: {},
    };

    constructor(props) {
        super(props);

        this.state = {
            isLoaded: false,
            latestModules: [],
            modules: [],
            showRefreshOverlay: false,
        };
    }

    componentDidMount() {
        const { event, ticketClasses, pagination } = this.props;
        const extras = getExtraData({ event, ticketClasses });

        const moreModules = pagination.pageNumber > pagination.pageCount;
        if (moreModules) {
            this._getStructuredContentModules(event.id, extras).then(
                (modules = []) => {
                    analytics.trackAnalyticsEvent({
                        category: GA_DEFAULT_CATEGORY,
                        action: GA_ACTION_PAGE_LOAD,
                        label: `${modules.length}`,
                        dimensions: { eventId: event.id },
                    });

                    this.setState({ modules });
                },
            );
        } else {
            this.setState({ modules: this.props.modules, isLoaded: true });
        }
    }

    _modulesChanged(latestModules) {
        const { modules } = this.state;
        if (modules.length != latestModules.length) {
            return true;
        }
        const currentModuleIds = modules.map((module) => module['id']);
        return latestModules.find(
            (module) => currentModuleIds.indexOf(module['id']) === -1,
        );
    }

    _getStructuredContentModules = (eventId, extras) => {
        return fetchEventStructuredContentApi(eventId, extras)
            .then((response) => {
                this.setState({
                    isLoaded: true,
                });
                return deepKeysToCamel(response).objects;
            })
            .catch((error) => {
                this.setState({
                    error,
                    isLoaded: true,
                });
            });
    };

    _syncStructuredContentModules(eventId, extras) {
        this._getStructuredContentModules(eventId, extras).then((modules) => {
            if (this._modulesChanged(modules)) {
                this.setState({
                    latestModules: modules,
                    showRefreshOverlay: true,
                });
            }
            setTimeout(
                this._syncStructuredContentModules.bind(this, eventId, extras),
                INTERVAL_TO_REFRESH_MODULES,
            );
        });
    }

    _refreshModules(event) {
        event.preventDefault();
        const { latestModules } = this.state;
        this.setState({
            modules: latestModules,
            showRefreshOverlay: false,
        });
    }

    _handleClick = (event, action, url) => () => {
        analytics.trackAnalyticsEvent({
            category: GA_DEFAULT_CATEGORY,
            action: `${action}`,
            label: `${url}`,
            dimensions: { eventId: event.id },
        });
    };

    renderModule(module) {
        const {
            event,
            attendeeId,
            userEmail,
            userFullName,
            zoomSdkChromeOriginTrialsToken,
        } = this.props;
        const { id, type, data } = module;
        const ModuleComponent = moduleTypeMap[type];

        return (
            ModuleComponent && (
                <div
                    className="dc-modules__item eds-l-mar-bot-12 eds-l-lg-mar-bot-14"
                    key={id}
                >
                    <ModuleComponent
                        attendeeId={attendeeId}
                        data={data}
                        event={event}
                        userEmail={userEmail}
                        userFullName={userFullName}
                        onClick={this._handleClick.bind(this)}
                        zoomSdkChromeOriginTrialsToken={
                            zoomSdkChromeOriginTrialsToken
                        }
                    />
                </div>
            )
        );
    }

    renderModules() {
        const { error, modules } = this.state;
        const { event, hideVirtualVenueContent } = this.props;

        if (error) {
            return <DigitalContent404 />;
        } else if (hideVirtualVenueContent || isEmpty(modules)) {
            return (
                <DigitalContentEmptyState
                    eventUrl={`/e/${this.props.event.id}`}
                    organizerUrl={`/o/${this.props.event.organizerId}`}
                />
            );
        }
        return modules.map((module) => this.renderModule(module, event));
    }

    renderRefreshOverlay() {
        const { showRefreshOverlay } = this.state;
        if (showRefreshOverlay) {
            return (
                <div className="dc-refresh-overlay">
                    {gettext('Some content has changed.')}{' '}
                    <a href="#" onClick={this._refreshModules.bind(this)}>
                        {gettext('Refresh')}
                    </a>
                </div>
            );
        }
    }

    render() {
        const { isLoaded, modules } = this.state;

        const showDownloadDisclaimer = hasModuleOfType(
            modules,
            MODULE_TYPE_FILE,
        );

        return (
            isLoaded && (
                <div className="dc-modules">
                    {this.renderModules()}
                    {this.renderRefreshOverlay()}
                    {showDownloadDisclaimer && <DownloadDisclaimer />}
                </div>
            )
        );
    }
}
